import { useApolloClient } from "@apollo/client";
import { getDataOrNull, headOrThrow } from "@msys/common";
import {
  DebouncedSearchInput,
  ListHeader,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  ModalOpenButton,
  useScreenWidth,
} from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useFormik } from "formik";
import React from "react";
import { TemplateSearchFiltersInput } from "../../../../clients/graphqlTypes.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { SwitchCollectionViewButton } from "../../../commons/button/SwitchCollectionViewButton.js";
import EmptyScreen from "../../../commons/EmptyScreen.js";
import { FilterButton } from "../../../commons/filters/FilterButton.js";
import { useAutoSave } from "../../../commons/hooks/useAutoSave.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../../commons/hooks/useCollectionView.js";
import { Page, PageTopbarItem } from "../../../commons/layout/Page.js";
import { PageContainer } from "../../../commons/layout/PageContainer.js";
import { ImportTemplatesIconButton } from "../../../features/templates/buttons/ImportTemplatesButton.js";
import {
  getNonTemplateFormValues,
  getTemplateFormValues,
  TemplateFiltersFormValues,
  templateFiltersValidationSchema,
} from "../../../features/templates/filters/TemplateFilterFields.js";
import { TemplateFilters } from "../../../features/templates/filters/TemplateFilters.js";
import { quoteTemplateSortingToSearchQuotesSorting } from "../../../features/templates/helpers.js";
import { CreateQuoteTemplateButton } from "../../../features/templates/quote/buttons/CreateTemplateQuoteButton.js";
import { TemplateQuotesFilterWithTemplateTypeModal } from "../../../features/templates/quote/TemplatesQuoteFilterModal.js";
import { useTemplatesQueryVariablesInUrl } from "../../../features/templates/quote/useTemplatesQueryVariables.js";
import { TemplateDocumentsList } from "../../../features/templates/TemplateDocumentsList.js";
import { useTemplateDocumentsAllAvailableActiveFilterChips } from "../../../features/templates/useTemplateDocumentsActiveFilterChips.js";
import { useTemplateDocumentsListBulkActions } from "../../../features/templates/useTemplateDocumentsListBulkActions.js";
import { useListViewCustomQuickFilters } from "../../../features/users/useListViewCustomQuickFilters.js";
import {
  TemplateQuotesSearchQueryVariables,
  useTemplateQuotesSearchQuery,
} from "./TemplateDocumentsAllAvailable.generated.js";
import {
  TemplateQuotes_TemplateFragment,
  TemplateQuotesQueryVariables,
} from "./TemplateQuotes.generated.js";

type TemplatesCollectionView = Extract<
  CollectionView,
  "table" | "list" | "gallery"
>;
const ALLOWED_VIEWS: TemplatesCollectionView[] = ["table", "list", "gallery"];

const FIXED_FILTERS = {
  sources: ["available" as const],
};

interface Props {
  submenuItems: PageTopbarItem[];
  pathToDocList: string;
}

export const TemplateDocumentsAllAvailable = ({
  submenuItems,
  pathToDocList,
}: Props) => {
  const currentUser = useUserData().currentUser!;
  const { t } = useTranslate(["Templates", "Global", "TemplatesSharing"]);

  const { isMinTablet } = useScreenWidth();

  const title = React.useMemo(
    (): string => t("All Available", { ns: "Templates" }),
    [t]
  );

  const {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    queryBaseVariables,
    searchTerm,
    setSearchTerm,
  } = useTemplatesQueryVariablesInUrl<TemplateSearchFiltersInput>({
    initialFilters: {},
  });

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<TemplatesCollectionView>(
      "templates",
      "table",
      "list"
    );

  const variables: Omit<TemplateQuotesSearchQueryVariables, "sorting"> &
    Pick<TemplateQuotesQueryVariables, "sorting"> = {
    ...queryBaseVariables,
    sorting,
    searchTerm,
    filters: {
      ...FIXED_FILTERS,
      ...filters,
      createdAfter: filters?.createdAfter?.format("YYYY-MM-DD"),
      createdBefore: filters?.createdBefore?.format("YYYY-MM-DD"),
    },
  };

  const client = useApolloClient();
  const query = useTemplateQuotesSearchQuery({
    client,
    variables: {
      ...variables,
      sorting: quoteTemplateSortingToSearchQuotesSorting(variables.sorting),
      filters: {
        ...variables.filters,
      },
    },
  });

  const items = getDataOrNull(
    (query.data ?? query.previousData)?.searchTemplates
  )?.edges.map(e => e.node);
  const totalCount =
    getDataOrNull((query.data ?? query.previousData)?.searchTemplates)
      ?.totalCount ?? 0;

  const [selectedItems, setSelectedItems] = React.useState<
    TemplateQuotes_TemplateFragment[]
  >([]);
  const resetSelectedItems = React.useCallback(() => setSelectedItems([]), []);

  const { actionButtons } = useTemplateDocumentsListBulkActions(
    "ALL_AVAILABLE",
    selectedItems,
    resetSelectedItems
  );

  // we need an inner formik state to not immediately apply filters on the list
  const formikProps = useFormik<TemplateFiltersFormValues>({
    initialValues: getTemplateFormValues(filters),
    validationSchema: templateFiltersValidationSchema,
    validateOnMount: true,
    onSubmit: async newValues => {
      setFilters(state => {
        return { ...state, ...newValues };
      });
    },
  });

  useAutoSave({
    enableReinitialize: true,
    initialValues: getTemplateFormValues(filters),
    formikProps,
  });

  const createButton = (
    <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
      <CreateQuoteTemplateButton
        pathToDocList={pathToDocList}
        templateTypeId={
          filters.templateTypeIds?.length === 1
            ? headOrThrow(filters.templateTypeIds)
            : undefined
        }
      />
    </RestrictedByOrganisationPermissionWithDebug>
  );

  const importButton = (
    <RestrictedByCapabilityWithDebug capability="TEMPLATE_IMPORT_EXPORT">
      <RestrictedByOrganisationPermissionWithDebug permission="MANAGE_TEMPLATE">
        <ImportTemplatesIconButton />
      </RestrictedByOrganisationPermissionWithDebug>
    </RestrictedByCapabilityWithDebug>
  );

  const switchViewButton = (
    <SwitchCollectionViewButton<TemplatesCollectionView>
      allowedViews={ALLOWED_VIEWS}
      activeView={activeView}
      setActiveView={setActiveView}
    />
  );

  const quickFilters = isMinTablet ? (
    <TemplateFilters
      formikProps={formikProps}
      templateSearchVariables={{
        searchTerm: variables.searchTerm,
        filters:
          variables.filters && getNonTemplateFormValues(variables.filters),
      }}
    />
  ) : undefined;

  const filterButton = (
    <ModalOpenButton
      Modal={TemplateQuotesFilterWithTemplateTypeModal}
      modalProps={{
        resetFilters,
        sorting,
        setSorting,
        filters,
        setFilters,
        initialTemplateFormValues: getTemplateFormValues(filters),
        templateSearchVariables: {
          searchTerm: variables.searchTerm,
          filters:
            variables.filters && getNonTemplateFormValues(variables.filters),
        },
      }}
    >
      <FilterButton />
    </ModalOpenButton>
  );

  const searchField = (
    <DebouncedSearchInput
      placeholder={t("Search", { ns: "Global" })}
      defaultValue={searchTerm}
      onChangeSearchTerm={newValue => setSearchTerm(newValue)}
      sx={theme => ({
        flexGrow: 1,
        maxWidth: theme.layout.searchFieldWidth.lg,
      })}
    />
  );

  const activeFilterChips = useTemplateDocumentsAllAvailableActiveFilterChips({
    filters,
    setFilters,
  });

  const listViewName = "TemplatesAllAvailable";
  const { customQuickFilterSelect, addCustomFilterButton } =
    useListViewCustomQuickFilters<TemplateSearchFiltersInput>(
      listViewName,
      filters,
      setFilters,
      [
        {
          filterName: t("Created by me", { ns: "Templates" }),
          filterValue: { createdByUserIds: [currentUser.id] },
        },
      ]
    );

  return (
    <Page
      title={title}
      subtitle={t("Document Templates", {
        ns: "Templates",
      })}
      submenuItems={submenuItems}
      action={actionButtons}
    >
      <PageContainer>
        <ListHeader
          elementsOrder="separate-rows"
          CreateButton={createButton}
          MenuButton={importButton}
          SwitchViewButton={switchViewButton}
          QuickFilter={
            quickFilters || customQuickFilterSelect ? (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: "center",
                  flexShrink: 1,
                  flexGrow: 0,
                }}
              >
                {quickFilters}
                {customQuickFilterSelect}
              </Stack>
            ) : undefined
          }
          FilterButton={filterButton}
          ActiveFilterChips={
            <ListHeaderActiveFilters
              activeFilterChips={activeFilterChips}
              collapsedContent={
                <Typography variant="body2">
                  {t("Active filters", { ns: "Global" })}
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      marginLeft: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {t("{count} results", {
                      ns: "Global",
                      count: activeFilterChips.length,
                    })}
                  </Typography>
                </Typography>
              }
              clearAllButton={
                <ListHeaderActiveFiltersActionButton
                  label={t("Clear all", { ns: "Global" })}
                  onClick={resetFilters}
                />
              }
              saveFiltersButton={addCustomFilterButton}
            />
          }
          SearchField={searchField}
          sx={{ mb: 2 }}
        />

        {!query.loading && items?.length === 0 ? (
          <EmptyScreen slug="templates-quotes" />
        ) : (
          <TemplateDocumentsList
            items={items ?? []}
            totalCount={totalCount}
            loading={query.loading}
            showSubscribedColumn={true}
            pathToDocList={pathToDocList}
            activeView={activeView}
            sorting={sorting}
            setSorting={setSorting}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            highlightedPropertyKeys={filters.properties?.map(p => p.key)}
          />
        )}
      </PageContainer>
    </Page>
  );
};
